/***
 * Created on 2023/10/18
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {apiGet, apiPost} from "../../service/api";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useCountdown} from "../../hook/countdown";
import {useTogglePopup} from "../../hook/toggle_popup";
import {useOnChange} from "common/Hook";
import {getErrorMessage} from 'common/ServiceCommon';
import Captcha from "../common/captcha";


function SmsActivation(props) {
    const { t } = useTranslation();
    const {seconds, setSeconds} = useCountdown(0);
    const {isShow, toggleAction, elemRef} = useTogglePopup();
    const [country, setCountry] = useState();
    const session = sessionStorage.getItem('signup_data');
    const signup_data = session ? JSON.parse(session) : {};
    const [code, setCode] = useState('');
    const [errorMessage, setErrorMessage] = useState();
    const {values, setValues, onChange} = useOnChange({
        captcha: '',
        activation_token: signup_data.activation_token,
        country_code: signup_data.country_code,
        mobile_number: signup_data.qid || signup_data.mobile_number,
        lang: localStorage.getItem('currentLang') || 'en'
    });
    const queryClient = useQueryClient();

    const country_code = useQuery({
        queryKey: ['get_country_code'],
        queryFn: async () => await apiGet('/ajax/country_code')
    });

    const countDown = useQuery({
        queryKey: ['get_countdown'],
        queryFn: async () => await apiGet('/ajax/countdown')
    });

    const sendSMSMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/send_mobile_code', data),
        onSuccess: data => {
            queryClient.invalidateQueries({
                queryKey: ['get_countdown']
            })
        }
    });

    const verifySMSMutation = useMutation({
        mutationFn: async (data) => await apiPost('/ajax/verify_mobile', data),
        onSuccess: data => {
            window.location.replace('/signup#account_activated');
            window.location.reload();
        },
        onError: (error, variables, context) => {
            queryClient.invalidateQueries({
                queryKey: ["get_captcha_image"]
            });
            setErrorMessage(getErrorMessage(t, error.cause?.code));
        }
    });

    const resendSMS = async () => {
        await sendSMSMutation.mutateAsync(values);
    }

    const verifySMS = async () => {
        setErrorMessage(null);
        const data = {
            ...values,
            code
        };
        await verifySMSMutation.mutateAsync(data);
    }

    useEffect(() => {
        if (country_code.data) {
            const country = country_code.data.find(c => c.country_code === signup_data?.country_code?.substring(1));
            setCountry(country);
        }
    }, [country_code.data])

    useEffect(() => {
        if (countDown.data && countDown.isSuccess) {
            const now = new Date().getTime();
            const value = Math.round((now - countDown.data?.countdown) / 1000) || 0;
            setSeconds(value > 60 ? 0 : 60 - value);
        }
    }, [countDown.data, countDown.isSuccess]);

    return (
        <section className="common-page-wrapper">
            <div className="common-page-header">{t('@Verify Activation Code@')}</div>
            <div className="common-page-body">
                <div className="common-form-group">
                    <p>{t('@The verification code was sent to@')}</p>
                    <div className="comp-input">
                        <div className="input-field--hybrid">
                            <div className={`comp-select ${isShow ? 'active' : ''}`} ref={elemRef} onClick={toggleAction}>
                                <div className="q-select cursor-pointer">
                                    <div className="q-select__label">{country?.country}(+{country?.country_code})</div>
                                    <div className="q-select__arrow"></div>
                                </div>
                                <div className="comp-dropdown">
                                    <ul className="dropdown-menu">
                                        <li className="dropdown-menu-section">
                                            {
                                                country_code?.data && country_code.data.map(country =>
                                                    <div className="dropdown-menu__item cursor-pointer" key={country._id}
                                                         onClick={()=>{
                                                             setValues(prev=> ({...prev, country_code: country.country_code}))
                                                             setCountry(country)
                                                         }}>{country.country}(+{country.country_code})</div>
                                                )
                                            }
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <input type="text" name='mobile_number' value={values.mobile_number} onChange={onChange}/>
                        </div>
                    </div>
                    <ul className="list-style-type-disc ml-16 mt-16">
                        <li className="pl-4 mb-10">{t('@Recommend you to finish the activation first then close this page.@')}</li>
                        <li className="pl-4">{t('@Please note that the QNAP ID (phone number) can only be used on QTS 4.3.4 or above.@')} <a href="">{t('@Learn more@')}</a></li>
                    </ul>
                </div>
                <div className="common-form-group">
                    <p>{t('@Check your sms and enter the code below:@')}</p>
                    <div className="comp-input">
                        <div className="input-field--hybrid">
                            <input type="text" className="input--must-fill" maxLength={6} pattern={/\d+/} name={'code'} value={code} onChange={e=>setCode(e.target.value)}/>
                            <button className="q-btn q-btn-default" disabled={seconds > 0} onClick={resendSMS}>{t('@Send@')} {seconds > 0 ? seconds : ''}</button>
                        </div>
                    </div>
                    {errorMessage &&<p className="text-error mt-10">{errorMessage}</p>}

                    <Captcha value={values.captcha} inputChangeHandler={onChange} resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>

                </div>
            </div>
            <div className="common-page-footer">
                <div className="q-btn-group btn-align-right">
                    <button className="q-btn q-btn--primary btn-medium btn-rounded-normal" onClick={verifySMS} disabled={code?.length !== 6}>{t('@Verify@')}</button>
                </div>
            </div>
        </section>
    );
}

export default SmsActivation;